/**
 * Persistent "you are impersonating" bar with a way back to the admin account.
 *
 * Mounted unconditionally at the App level and renders nothing unless the
 * server says the current session is an impersonation, so the admin always has
 * an escape hatch no matter which page the impersonated user lands on.
 *
 * Returning to admin does a full page load rather than patching client state.
 * That's deliberate: identity is cached in several places that no component can
 * reach (the React Query cache, the realtime access-token ref inside
 * AuthProvider, and the Supabase Realtime socket's auth). A reload re-bootstraps
 * every one of them from the swapped cookie, which rules out the entire class of
 * bugs where one user's data or WebSocket bleeds into the other's session.
 */

import { useState, useEffect } from 'react';
import { ShieldAlert, LogOut, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { stopImpersonating } from '@/services/authService';

/**
 * Height of the bar. Exported through the --impersonation-offset custom property
 * so the app chrome can make room for it: AppLayout's Header is `fixed top-0`
 * and its <main> carries a hardcoded top padding, neither of which know anything
 * about us. Both read the property (defaulting to 0px), so when no impersonation
 * is active the layout is byte-for-byte what it was before.
 */
const BAR_HEIGHT = '40px';

export function ImpersonationBanner() {
  const { impersonator, user } = useAuth();
  const { showError } = useToast();
  const [isStopping, setIsStopping] = useState(false);

  const isImpersonating = impersonator?.isImpersonating === true;

  // Reserve space for the bar while it is showing, and give it back when it is
  // not. Must run as an effect (not during render) and must clean up on unmount,
  // or returning to the admin account would leave a 40px gap under the header.
  useEffect(() => {
    const root = document.documentElement;
    if (!isImpersonating) {
      root.style.removeProperty('--impersonation-offset');
      return;
    }
    root.style.setProperty('--impersonation-offset', BAR_HEIGHT);
    return () => root.style.removeProperty('--impersonation-offset');
  }, [isImpersonating]);

  if (!isImpersonating) return null;

  const handleStop = async () => {
    if (isStopping) return;
    setIsStopping(true);

    try {
      await stopImpersonating();
      // Cookie now points back at the admin's original session. Hard-navigate
      // so every in-memory cache is rebuilt under the admin identity. Land on
      // the admin page they started from rather than wherever the impersonated
      // user happened to be.
      window.location.assign('/admin');
    } catch (error: any) {
      setIsStopping(false);
      showError(
        'Failed to return to your admin account',
        error?.message || 'Try reloading the page, or log in again.'
      );
    }
  };

  const adminLabel =
    impersonator.impersonatorName || impersonator.impersonatorEmail || 'your admin account';

  const impersonatedLabel =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'this user';

  return (
    // Fixed, not sticky: AppLayout puts `overflow: hidden` and a locked height on
    // html/body/#root (body.app-layout in index.css), which clips anything in the
    // normal flow above it. And z-[60] to sit above the app Header, which is
    // `fixed top-0 ... z-50` and would otherwise paint straight over this bar.
    <div
      role="status"
      aria-live="polite"
      style={{ height: BAR_HEIGHT }}
      className="fixed inset-x-0 top-0 z-[60] flex items-center border-b border-amber-600 bg-amber-500"
    >
      <div className="flex w-full items-center justify-between gap-2 px-4 text-sm text-amber-950 sm:px-6">
        <div className="flex min-w-0 items-center gap-2">
          <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="truncate">
            <span className="font-semibold">Viewing as {impersonatedLabel}</span>
            <span className="hidden sm:inline">
              {' '}
              — everything you do is recorded against {adminLabel}.
            </span>
          </span>
        </div>

        <button
          type="button"
          onClick={handleStop}
          disabled={isStopping}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-amber-900 px-3 py-1 text-xs font-medium text-white shadow-sm transition-colors hover:bg-amber-950 disabled:opacity-60"
        >
          {isStopping ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          Return to admin
        </button>
      </div>
    </div>
  );
}

export default ImpersonationBanner;
